import { Facebook, Instagram } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, SITE } from "@/constants/site";
import { scrollToSection, useLenis } from "@/components/LenisProvider";

export default function Footer() {
  const lenis = useLenis();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-olive pb-10 pt-16 text-cream" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-sm">
            <Logo light />
            <p className="mt-6 font-serif text-2xl italic leading-snug text-cream/80">
              {SITE.tagline}.
            </p>
            <div className="mt-7 flex items-center gap-3">
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook-link"
                aria-label="Facebook de Toscana Cocinas Integrales"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream/80 transition-colors duration-300 hover:bg-cream hover:text-olive"
              >
                <Facebook className="h-5 w-5" strokeWidth={1.5} />
              </a>
              {SITE.instagram && (
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram-link"
                  aria-label="Instagram de Toscana Cocinas Integrales"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream/80 transition-colors duration-300 hover:bg-cream hover:text-olive"
                >
                  <Instagram className="h-5 w-5" strokeWidth={1.5} />
                </a>
              )}
              {SITE.tiktok && (
                <a
                  href={SITE.tiktok}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok-link"
                  aria-label="TikTok de Toscana Cocinas Integrales"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-cream/20 text-cream/80 transition-colors duration-300 hover:bg-cream hover:text-olive"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.25-.02-.5-.02-.76.02-1.74.72-3.45 1.92-4.72 1.42-1.51 3.5-2.38 5.57-2.19.16-1.63.94-3.18 2.2-4.26 1.34-1.14 3.14-1.78 4.9-1.72z" />
                  </svg>
                </a>
              )}
            </div>
          </div>

          <nav className="flex flex-col gap-3" data-testid="footer-nav">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-cream/40">
              Navegación
            </p>
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`footer-nav-${link.label.toLowerCase()}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(lenis, link.href);
                }}
                className="text-cream/75 transition-colors duration-200 hover:text-cream"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex flex-col gap-3" data-testid="footer-contact">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-cream/40">
              Contacto
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="footer-phone-link"
              className="text-cream/75 transition-colors duration-200 hover:text-cream"
            >
              Llámanos · {SITE.phoneDisplay}
            </a>
            <a
              href={SITE.whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-link"
              className="text-cream/75 transition-colors duration-200 hover:text-cream"
            >
              WhatsApp · {SITE.whatsappDisplay}
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email-link"
              className="text-cream/75 transition-colors duration-200 hover:text-cream"
            >
              {SITE.email}
            </a>
            <span className="text-cream/75">{SITE.address}</span>
            <span className="text-cream/75">Cerramos días feriados oficiales de México</span>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-3 border-t border-cream/10 pt-8 text-sm text-cream/50 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">
            © {year} {SITE.fullName}
          </p>
          <p>Diseñamos el corazón de tu hogar, desde nuestro taller a tu casa.</p>
        </div>
      </div>
    </footer>
  );
}
